import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  Autocomplete,
  Card,
  CardHeader,
  CardContent,
  FormHelperText,
  Divider,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import InputMask from "react-input-mask";
import PhoneField from "./PhoneField";
import { fetchData } from "../api/client";
import {
  formatStoredPhoneForInput,
  getPhoneValidationMessage,
  toE164Digits,
  isPhoneSentinel,
} from "../utils/phone";
import { useCompanies } from "../hooks/useCompanies";
import { useClientTags } from "../hooks/useClientTags";

/** ===== Helpers de transformação =====
 *  Tipo: 1 = PJ, 0 = PF
 */
const TIPO_STR_TO_NUM = (v) => (v === "Pessoa Jurídica" ? 1 : v === "Pessoa Física" ? 0 : null);

const STATUS_STR_TO_NUM = (v) => (v === "Ativo" ? 1 : v === "Inativo" ? 0 : null);

// aceita number ou "123" -> 123 | senão null
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Garante que o valor no Select seja SEMPRE o rótulo esperado
const normalizeTipoToLabel = (v, fallback = "Pessoa Física") => {
  const n = toNum(v);
  if (n === 1) return "Pessoa Jurídica"; // 1 = PJ
  if (n === 0) return "Pessoa Física";   // 0 = PF

  const s = String(v ?? "").toLowerCase();
  if (s.includes("jur")) return "Pessoa Jurídica";
  if (s.includes("fís") || s.includes("fis")) return "Pessoa Física";

  return fallback;
};

const normalizeStatusToLabel = (v, fallback = "Ativo") => {
  const n = toNum(v);
  if (n === 1) return "Ativo";
  if (n === 0) return "Inativo";

  const s = String(v ?? "").toLowerCase();
  if (s.includes("inativo")) return "Inativo";
  if (s.includes("ativo")) return "Ativo";

  return fallback;
};

const AddClientForm = ({
  client,
  onClose,
  onSubmit,
  isFetching,
  defaultProjectId = null,
  onDuplicateDetected,
}) => {
  const isEditMode = Boolean(client && client.id);
  const [formData, setFormData] = useState({
    tipo: "Pessoa Física",
    status: "Ativo",
    id_projeto: defaultProjectId ?? null,
    tags: [],
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(null);
  const [formMode, setFormMode] = useState(isEditMode ? "complete" : "quick");
  const [tagSearch, setTagSearch] = useState("");

  const { data: tagOptions } = useClientTags({ search: tagSearch, limit: 200, includeUsed: true });

  // Empresas / Projetos (mesma fonte do AddProductForm)
  const {
    data: companiesQuery = { companies: [], pagination: { total: 0 } },
    isLoading: companiesLoading,
  } = useCompanies(1, 500, "");
  const companies = companiesQuery?.companies || [];

  // Re-hidrata (normaliza) quando o detalhe chega da API
  useEffect(() => {
    if (!client) return;

    setFormData((prev) => {
      const tipoStr = normalizeTipoToLabel(client.tipo, prev.tipo || "Pessoa Física");
      const statusStr = normalizeStatusToLabel(client.status, prev.status || "Ativo");
      const rawTags = client.tags ?? "";
      const parsedTags =
        typeof rawTags === "string"
          ? rawTags.split(",").map((t) => t.trim()).filter(Boolean)
          : Array.isArray(rawTags)
          ? rawTags.map((t) => String(t).trim()).filter(Boolean)
          : [];

      return {
        ...prev,
        ...client,
        // normalizações de nomes/campos
        razao_social: client.razao_social ?? client.razaosocial ?? prev.razao_social ?? "",
        // selects como string para bater com as opções
        tipo: tipoStr,
        status: statusStr,
        tags: parsedTags,
        telefone: client.telefone
          ? formatStoredPhoneForInput(String(client.telefone))
          : prev.telefone ?? "",
        celular: client.celular
          ? formatStoredPhoneForInput(String(client.celular))
          : prev.celular ?? "",
        whatsapp: client.whatsapp
          ? formatStoredPhoneForInput(String(client.whatsapp))
          : prev.whatsapp ?? "",
      };
    });
  }, [client]);

  useEffect(() => {
    setFormMode(isEditMode ? "complete" : "quick");
  }, [isEditMode]);

  const sections = [
    {
      title: "Tipo de Cliente",
      subtitle: "Defina se o cadastro é de pessoa física ou jurídica.",
      fields: [
        {
          name: "tipo",
          type: "select",
          required: true,
          options: ["Pessoa Física", "Pessoa Jurídica"],
        },
      ],
    },
    {
      title: "Dados Pessoais",
      subtitle: "Informações básicas do contato quando o cliente é pessoa física.",
      fields: [
        { name: "nome", type: "text", required: true },
        { name: "apelido", type: "text", required: false },
        { name: "data_nascimento", type: "date", required: false },
        { name: "sexo", type: "select", required: false, options: ["Masculino", "Feminino", "Outro"] },
        { name: "cpf", type: "text", required: false },
        { name: "rg", type: "text", required: false },
        { name: "estado_civil", type: "select", required: false, options: ["Solteiro", "Casado", "Divorciado", "Viúvo"] },
      ],
    },
    {
      title: "Dados Empresariais",
      subtitle: "Informações da empresa quando o cliente é pessoa jurídica.",
      fields: [
        { name: "razao_social", type: "text", required: true },
        { name: "nome_fantasia", type: "text", required: false },
        { name: "cnpj", type: "text", required: true },
        { name: "inscricao_estadual", type: "text", required: false },
      ],
    },
    {
      title: "Contato",
      subtitle: "Canais de contato preferenciais do cliente.",
      fields: [
        { name: "email", type: "email", required: false },
        { name: "telefone", type: "text", required: false },
        { name: "celular", type: "text", required: false },
        { name: "whatsapp", type: "text", required: false },
        { name: "linkedin", type: "text", required: false },
      ],
    },
    {
      title: "Endereço",
      subtitle: "Endereço principal do cliente para faturamento e entrega.",
      fields: [
        { name: "cep", type: "text", required: false },
        { name: "cidade", type: "text", required: false },
        { name: "uf", type: "text", required: false },
        { name: "endereco", type: "text", required: false },
        { name: "bairro", type: "text", required: false },
        { name: "numero", type: "text", required: false },
        { name: "complemento", type: "text", required: false },
        { name: "referencia", type: "text", required: false },
      ],
    },
    {
      title: "Dados Complementares",
      subtitle: "Vincule o cliente a uma empresa/projeto e adicione observações.",
      fields: [
        { name: "id_projeto", type: "select", required: false },
        { name: "status", type: "select", required: true, options: ["Ativo", "Inativo"] },
        { name: "tags", type: "tags", required: false },
        { name: "tiposanguineo", type: "select", required: false, options: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"] },
        { name: "preferencia_contato", type: "select", required: false, options: ["Telefone", "E-mail", "WhatsApp"] },
        { name: "observacao", type: "textarea", required: false },
      ],
    },
  ];

  const handleCepChange = async (e) => {
    const cepValue = e.target.value;
    const cep = cepValue.replace(/\D/g, "");
    setFormData((prev) => ({ ...prev, cep: cepValue }));

    if (cep.length === 8) {
      try {
        const res = await fetchData(`/cep/lookup?cep=${cep}`, "GET");
        const data = res?.data || res;
        setFormData((prev) => ({
          ...prev,
          cidade: data.city || data.localidade || "",
          uf: data.uf || "",
          endereco: data.logradouro || "",
          bairro: data.bairro || "",
        }));
        setErrors((prev) => ({ ...prev, cep: "" }));
      } catch {
        setErrors((prev) => ({ ...prev, cep: "CEP inválido ou não encontrado" }));
      }
    } else if (cepValue && cepValue.length < 9) {
      setErrors((prev) => ({ ...prev, cep: "CEP inválido" }));
    } else {
      setErrors((prev) => ({ ...prev, cep: "" }));
    }
  };

  const validateField = (name, value) => {
    if (!value) return "";

    if (name === "email" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "E-mail inválido";
    }

    if (name === "cep") {
      const digits = String(value).replace(/\D/g, "");
      if (digits.length !== 8) return "CEP inválido";
      return "";
    }

    if (name === "cpf") {
      const digits = String(value).replace(/\D/g, "");
      if (digits.length !== 11) return "CPF deve ter 11 dígitos";

      const invalidCpfs = [
        "00000000000",
        "11111111111",
        "22222222222",
        "33333333333",
        "44444444444",
        "55555555555",
        "66666666666",
        "77777777777",
        "88888888888",
        "99999999999",
      ];
      if (invalidCpfs.includes(digits)) return "CPF inválido";

      let sum = 0;
      for (let i = 0; i < 9; i += 1) {
        sum += parseInt(digits.charAt(i), 10) * (10 - i);
      }
      let rev = 11 - (sum % 11);
      if (rev === 10 || rev === 11) rev = 0;
      if (rev !== parseInt(digits.charAt(9), 10)) return "CPF inválido";

      sum = 0;
      for (let i = 0; i < 10; i += 1) {
        sum += parseInt(digits.charAt(i), 10) * (11 - i);
      }
      rev = 11 - (sum % 11);
      if (rev === 10 || rev === 11) rev = 0;
      if (rev !== parseInt(digits.charAt(10), 10)) return "CPF inválido";

      return "";
    }

    if (name === "cnpj") {
      const digits = String(value).replace(/\D/g, "");
      if (digits.length !== 14) return "CNPJ deve ter 14 dígitos";

      const invalidCnpjs = [
        "00000000000000",
        "11111111111111",
        "22222222222222",
        "33333333333333",
        "44444444444444",
        "55555555555555",
        "66666666666666",
        "77777777777777",
        "88888888888888",
        "99999999999999",
      ];
      if (invalidCnpjs.includes(digits)) return "CNPJ inválido";

      const calcCnpjDigit = (base) => {
        let sum = 0;
        let pos = base.length - 7;
        for (let i = base.length; i >= 1; i -= 1) {
          sum += parseInt(base.charAt(base.length - i), 10) * pos;
          pos -= 1;
          if (pos < 2) pos = 9;
        }
        const result = sum % 11;
        return result < 2 ? 0 : 11 - result;
      };

      const base = digits.substring(0, 12);
      const digit1 = calcCnpjDigit(base);
      const digit2 = calcCnpjDigit(base + digit1);
      if (digits !== base + String(digit1) + String(digit2)) return "CNPJ inválido";

      return "";
    }

    if (name === "uf") {
      const uf = String(value).trim().toUpperCase();
      if (uf && uf.length !== 2) return "UF deve ter 2 letras";
      return "";
    }

    if (name === "numero") {
      const onlyDigits = String(value).replace(/\D/g, "");
      if (!onlyDigits) return "Número inválido";
      return "";
    }

    if (name === "linkedin") {
      const trimmed = String(value).trim();
      if (trimmed && !/^https?:\/\/.+/i.test(trimmed)) {
        return "Informe uma URL válida (começando com http ou https)";
      }
      return "";
    }

    if (name === "rg") {
      const cleaned = String(value).replace(/[.-]/g, "").trim();
      if (cleaned && cleaned.length < 5) return "RG inválido";
      return "";
    }

    if (name === "telefone" || name === "celular" || name === "whatsapp") {
      return getPhoneValidationMessage(value, "BR");
    }

    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: error }));
    setApiError(null);
  };

  const handleCompanyChange = (_evt, companyOption) => {
    setFormData((prev) => ({
      ...prev,
      id_projeto: companyOption ? companyOption.id : null,
    }));
    setApiError(null);
  };

  const buildDuplicateCheckQuery = (payload, excludeId = null) => {
    const sp = new URLSearchParams();
    const idProjeto = payload?.id_projeto ?? formData?.id_projeto;
    if (idProjeto) sp.set("id_projeto", String(idProjeto));
    if (payload?.email) sp.set("email", String(payload.email));
    if (payload?.cpf) sp.set("cpf", String(payload.cpf));
    if (payload?.cnpj) sp.set("cnpj", String(payload.cnpj));
    if (payload?.celular) sp.set("celular", String(payload.celular));
    if (payload?.telefone) sp.set("telefone", String(payload.telefone));
    if (payload?.whatsapp) sp.set("whatsapp", String(payload.whatsapp));
    if (payload?.id_interno) sp.set("id_interno", String(payload.id_interno));
    if (excludeId) sp.set("exclude_id", String(excludeId));
    return sp.toString();
  };

  const formatDuplicateError = (duplicates = []) => {
    if (!Array.isArray(duplicates) || duplicates.length === 0) {
      return "Já existe cliente com os dados informados neste projeto.";
    }
    const top = duplicates.slice(0, 3);
    const names = top.map((d) => `#${d.id} ${d.nome || "Sem nome"}`).join(" | ");
    return `Cliente possivelmente duplicado encontrado: ${names}.`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // validações (obrigatórios + formato)
    const newErrors = {};
    const isPessoaFisica = formData.tipo === "Pessoa Física";
    const isPessoaJuridica = formData.tipo === "Pessoa Jurídica";

    sections.forEach((section) => {
      if (
        (isPessoaFisica && section.title === "Dados Empresariais") ||
        (isPessoaJuridica && section.title === "Dados Pessoais")
      ) {
        return;
      }

      section.fields.forEach((field) => {
        const value = formData[field.name];

        if (field.required) {
          const isEmpty =
            value === null ||
            value === undefined ||
            (typeof value === "string" && value.trim() === "");
          if (isEmpty) {
            newErrors[field.name] = "Campo obrigatório";
            return;
          }
        }

        const err = validateField(field.name, value);
        if (err) newErrors[field.name] = err;
      });
    });

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // desnormaliza para o payload da API
    const payload = { ...formData };

    // tags (UI usa array) -> string CSV (API usa string)
    if (Array.isArray(payload.tags)) {
      const cleaned = payload.tags.map((t) => String(t || "").trim()).filter(Boolean);
      payload.tags = cleaned.length ? cleaned.join(", ") : "";
    }

    // tipo -> número (tolerante) | 1=PJ 0=PF
    const tipoNum = TIPO_STR_TO_NUM(formData.tipo);
    if (tipoNum !== null) payload.tipo = tipoNum;
    else {
      const n = toNum(formData.tipo);
      if (n === 0 || n === 1) payload.tipo = n;
    }

    // status -> número (tolerante)
    const statusNum = STATUS_STR_TO_NUM(formData.status);
    if (statusNum !== null) payload.status = statusNum;
    else {
      const n = toNum(formData.status);
      if (n === 0 || n === 1) payload.status = n;
    }

    // alinhar nome de campo da razão social com a API
    if (payload.razao_social !== undefined) {
      payload.razaosocial = payload.razao_social; // API usa 'razaosocial'
      delete payload.razao_social;
    }

    ["telefone", "celular", "whatsapp"].forEach((k) => {
      const v = payload[k];
      if (v && String(v).trim() && !isPhoneSentinel(String(v))) {
        const e164 = toE164Digits(v, "BR");
        if (e164) payload[k] = e164;
      }
    });

    if (onSubmit) {
      try {
        if (!isEditMode) {
          const query = buildDuplicateCheckQuery(payload);
          if (query.includes("id_projeto=")) {
            const dupResp = await fetchData(`/clients/duplicate-check?${query}`, "GET");
            const dupData = dupResp?.data || dupResp;
            if (dupData?.hasDuplicates) {
              const duplicates = dupData?.duplicates || [];
              if (typeof onDuplicateDetected === "function") {
                onDuplicateDetected({ duplicates, attemptedPayload: payload, phase: "precheck" });
              } else {
                const msg = formatDuplicateError(duplicates);
                setApiError(msg);
              }
              return;
            }
          }
        }

        await onSubmit(payload);
        setApiError(null);
      } catch (err) {
        const unknownTags =
          err?.data?.unknown ||
          err?.unknown ||
          err?.data?.data?.unknown ||
          null;

        // Normaliza mensagem geral
        const backendMsg =
          err?.data?.message ||
          err?.data ||
          err?.message ||
          "Erro ao salvar o cliente.";

        if (Array.isArray(unknownTags) && unknownTags.length) {
          setApiError(`Uma ou mais tags não são permitidas: ${unknownTags.join(", ")}`);
          return;
        }

        if (
          typeof onDuplicateDetected === "function" &&
          (err?.code === "errors.clients.duplicate" || err?.data?.code === "errors.clients.duplicate")
        ) {
          const duplicates = err?.data?.duplicates || err?.data?.data?.duplicates || err?.duplicates || [];
          onDuplicateDetected({ duplicates, attemptedPayload: payload, phase: "submit" });
          return;
        }

        setApiError(
          typeof backendMsg === "string"
            ? backendMsg
            : "Erro ao salvar o cliente. Verifique os campos obrigatórios."
        );

        // Tenta mapear erros para campos específicos (ex.: Column 'email' cannot be null)
        const text = String(
          err?.data?.details || err?.data?.message || err?.data || err?.message || ""
        ).toLowerCase();
        const fieldHints = {
          email: "Houve um erro ao salvar o e-mail. Verifique o valor informado.",
          nome: "Informe o nome do cliente.",
          razaosocial: "Informe a razão social da empresa.",
          razao_social: "Informe a razão social da empresa.",
          cnpj: "Informe o CNPJ da empresa.",
          cpf: "Informe um CPF válido e não duplicado.",
          celular: "Informe um celular válido e não duplicado.",
          telefone: "Informe um telefone válido e não duplicado.",
          whatsapp: "Informe um WhatsApp válido e não duplicado.",
          id_interno: "Este ID interno já está em uso para outro cliente.",
        };

        const fieldErrorsFromApi = {};
        Object.keys(fieldHints).forEach((fieldKey) => {
          if (text.includes(fieldKey)) {
            const mappedKey =
              fieldKey === "razaosocial" ? "razao_social" : fieldKey;
            fieldErrorsFromApi[mappedKey] = fieldHints[fieldKey];
          }
        });

        if (Object.keys(fieldErrorsFromApi).length) {
          setErrors((prev) => ({ ...prev, ...fieldErrorsFromApi }));
        }
      }
    }
  };

  const isSubmitting = Boolean(isFetching);

  // Desabilita o botão salvar se houver erros ou campos obrigatórios vazios
  const hasClientSideErrors = Object.values(errors).some(Boolean);
  const hasRequiredMissing = sections.some((section) => {
    if (
      (formData.tipo === "Pessoa Física" && section.title === "Dados Empresariais") ||
      (formData.tipo === "Pessoa Jurídica" && section.title === "Dados Pessoais")
    ) {
      return false;
    }

    return section.fields.some((field) => {
      if (!field.required) return false;
      const value = formData[field.name];
      return (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      );
    });
  });

  const disableSave = isSubmitting || hasClientSideErrors || hasRequiredMissing;

  const quickFieldNames = new Set([
    "tipo",
    "nome",
    "apelido",
    "cpf",
    "razao_social",
    "cnpj",
    "email",
    "celular",
    "telefone",
    "whatsapp",
    "cidade",
    "uf",
    "id_projeto",
    "status",
  ]);

  const visibleFields = (section) => {
    if (formMode === "complete") return section.fields;
    return section.fields.filter((f) => quickFieldNames.has(f.name));
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      elevation={3}
      sx={{
        p: 2,
        width: "95%",
        m: "0 auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography variant="h6">
          {client && client.id ? "Editar Cliente" : "Adicionar Cliente"}
        </Typography>
      </Box>

      {!isEditMode && (
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            color="primary"
            value={formMode}
            onChange={(_e, next) => {
              if (next) setFormMode(next);
            }}
          >
            <ToggleButton value="quick">Cadastro rápido</ToggleButton>
            <ToggleButton value="complete">Cadastro completo</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {apiError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{apiError}</Alert>
        </Box>
      )}

      {sections.map((section) => {
        if (
          (formData.tipo === "Pessoa Física" && section.title === "Dados Empresariais") ||
          (formData.tipo === "Pessoa Jurídica" && section.title === "Dados Pessoais")
        ) {
          return null;
        }

        const fieldsToRender = visibleFields(section);
        if (!fieldsToRender.length) return null;

        return (
          <Card key={section.title} variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={section.title}
              subheader={section.subtitle}
              sx={{
                pb: 0.5,
                "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 },
                "& .MuiCardHeader-subheader": { fontSize: 13 },
              }}
            />
            <CardContent sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {fieldsToRender.map((field) => {
                  const labelBase = field.name.replace(/_/g, " ");
                  const label =
                    labelBase.charAt(0).toUpperCase() + labelBase.slice(1) + (field.required ? " *" : "");

                  return (
                    <Grid item xs={12} md={6} key={field.name}>
                      {field.type === "select" ? (
                        field.name === "id_projeto" ? (
                          <Autocomplete
                            options={companies}
                            getOptionLabel={(o) => o?.nome || o?.name || ""}
                            value={
                              companies.find(
                                (c) => String(c.id) === String(formData.id_projeto)
                              ) || null
                            }
                            onChange={handleCompanyChange}
                            loading={companiesLoading}
                            loadingText="Carregando empresas..."
                            isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Empresa"
                                placeholder="Selecione a empresa"
                                fullWidth
                              />
                            )}
                          />
                        ) : (
                          <FormControl fullWidth variant="outlined" error={!!errors[field.name]}>
                            <InputLabel>{label}</InputLabel>
                            <Select
                              name={field.name}
                              value={formData[field.name] ?? ""}
                              onChange={handleChange}
                              label={label}
                            >
                              {(field.options || []).map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[field.name] && (
                              <FormHelperText>{errors[field.name]}</FormHelperText>
                            )}
                          </FormControl>
                        )
                      ) : field.type === "textarea" ? (
                        <TextField
                          label={label}
                          name={field.name}
                          value={formData[field.name] ?? ""}
                          onChange={handleChange}
                          multiline
                          rows={4}
                          fullWidth
                          error={!!errors[field.name]}
                          helperText={errors[field.name]}
                        />
                      ) : field.type === "tags" ? (
                        <Autocomplete
                          multiple
                          filterSelectedOptions
                          options={(tagOptions || [])
                            .filter((t) => (t?.status == null ? true : Number(t.status) === 1))
                            .map((t) => t?.nome)
                            .filter(Boolean)}
                          value={Array.isArray(formData.tags) ? formData.tags : []}
                          onChange={(_e, next) => {
                            const cleaned = (next || [])
                              .map((t) => String(t || "").trim())
                              .filter(Boolean);
                            const uniq = Array.from(new Set(cleaned));
                            setFormData((prev) => ({ ...prev, tags: uniq }));
                          }}
                          onInputChange={(_e, v) => setTagSearch(v)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tags"
                              placeholder="Selecione tags"
                              fullWidth
                            />
                          )}
                        />
                      ) : field.name === "cep" ? (
                        <InputMask
                          mask="99999-999"
                          value={formData[field.name] ?? ""}
                          onChange={handleCepChange}
                          maskChar={null}
                        >
                          {(inputProps) => (
                            <TextField
                              {...inputProps}
                              label="CEP"
                              fullWidth
                              error={!!errors[field.name]}
                              helperText={errors[field.name]}
                            />
                          )}
                        </InputMask>
                      ) : field.name === "cpf" ? (
                        <InputMask
                          mask="999.999.999-99"
                          value={formData[field.name] ?? ""}
                          onChange={handleChange}
                          maskChar={null}
                        >
                          {(inputProps) => (
                            <TextField
                              {...inputProps}
                              label={label}
                              name={field.name}
                              fullWidth
                              error={!!errors[field.name]}
                              helperText={errors[field.name]}
                            />
                          )}
                        </InputMask>
                      ) : field.name === "cnpj" ? (
                        <InputMask
                          mask="99.999.999/9999-99"
                          value={formData[field.name] ?? ""}
                          onChange={handleChange}
                          maskChar={null}
                        >
                          {(inputProps) => (
                            <TextField
                              {...inputProps}
                              label={label}
                              name={field.name}
                              fullWidth
                              error={!!errors[field.name]}
                              helperText={errors[field.name]}
                            />
                          )}
                        </InputMask>
                      ) : field.name === "telefone" ||
                        field.name === "celular" ||
                        field.name === "whatsapp" ? (
                        <PhoneField
                          name={field.name}
                          label={label}
                          value={formData[field.name] ?? ""}
                          onChange={handleChange}
                          error={!!errors[field.name]}
                          helperText={errors[field.name]}
                          required={!!field.required}
                        />
                      ) : (
                        <TextField
                          label={label}
                          name={field.name}
                          value={formData[field.name] ?? ""}
                          onChange={handleChange}
                          type={field.type}
                          InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
                          fullWidth
                          error={!!errors[field.name]}
                          helperText={errors[field.name]}
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      <Divider sx={{ mt: 1, mb: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={disableSave}>
          {isSubmitting ? <CircularProgress size={20} /> : "Salvar"}
        </Button>
      </Box>
    </Paper>
  );
};

export default AddClientForm;
